export const STATUS_OPTIONS = [
    "INVENTORY",
    "SHIPPED",
    "ARRIVED",
    "DEPLOYED",
    "REPAIR",
    "RETIRED",
];

export function findBySticker(items, input, idField) {
    const key = String(input || "").trim().toLowerCase();
    if (key.length < 8) return {match: null, matches: []};
    const prefix = key.slice(0, 8);
    const matches = (items || []).filter((item) =>
        String(item?.[idField] || "").toLowerCase().startsWith(prefix)
    );
    if (key.length > 8) {
        const exact = matches.filter((item) => String(item[idField]).toLowerCase() === key);
        if (exact.length === 1) return {match: exact[0], matches: exact};
    }
    return {match: matches.length === 1 ? matches[0] : null, matches};
}

export function pendingReplaceMessage(unit) {
    if (unit?.replacesUnitId) {
        return `Staged to replace ${sticker(unit.replacesUnitId)}. This unit takes the customer cap when it next boots.`;
    }
    if (unit?.replacedByUnitId) {
        return `Replacement ${sticker(unit.replacedByUnitId)} is staged. This unit keeps the cap until that box boots.`;
    }
    return null;
}

export function sticker(id) {
    return id ? String(id).slice(0, 8).toUpperCase() : "—";
}

/** Cap names are often set to the unit sticker (first 8 of unitId). Do not use those as a cap label. */
export function isUnitStickerName(name) {
    return typeof name === "string" && /^[0-9a-f]{8}$/i.test(name.trim());
}

/** Short cap identity: first 8 of capId, not the unit sticker stored in cap.name. */
export function capShortId(cap, capId) {
    const id = (cap && cap.capId) || capId;
    return sticker(id);
}

/** Jobsite label when the cap has a real name/neighborhood, not the unit sticker. */
export function capSiteLabel(cap) {
    if (!cap) return null;
    const rawName = cap.name && String(cap.name).trim();
    const name =
        rawName && rawName !== "New Cap" && !isUnitStickerName(rawName) ? rawName : null;
    if (cap.neighborhood && name) return `${cap.neighborhood}, ${name}`;
    if (cap.neighborhood) return cap.neighborhood;
    if (name) return name;
    return null;
}

export function formatDate(d) {
    if (!d) return "—";
    const date = new Date(d);
    return isNaN(date.getTime()) ? String(d) : date.toLocaleString();
}

export function formatDateOnly(d) {
    if (!d) return "—";
    const date = new Date(d);
    return isNaN(date.getTime()) ? String(d) : date.toLocaleDateString();
}

export function productType(architecture) {
    if (architecture && architecture.includes("4CAM")) return "PRO";
    if (architecture && architecture.includes("2CAM")) return "JobSight";
    return "Legacy";
}

export function formatPortSsh(port) {
    if (port == null || port === "") return "—";
    const n = Number(port);
    if (!Number.isFinite(n)) return String(port);
    return `${n} / ${n - 5780}`;
}

export function sshPort(port) {
    if (port == null || port === "") return null;
    const n = Number(port);
    return Number.isFinite(n) ? n - 5780 : null;
}

/**
 * Warehouse / field lifecycle. BUILD + no builder is inventory;
 * BUILD + builder is shipped (or arrived if already marked);
 * ACTIVE is deployed. REPAIR/RETIRED stay stored.
 */
export function unitLifecycle(unit, cap) {
    const stored = (unit?.status || "").toUpperCase();
    if (stored === "REPAIR" || stored === "RETIRED") return stored;
    if (cap?.status === "ACTIVE") return "DEPLOYED";
    if (cap?.status === "BUILD") {
        if (!cap.builderId) return "INVENTORY";
        if (stored === "ARRIVED") return "ARRIVED";
        return "SHIPPED";
    }
    return stored || "INVENTORY";
}

export function statusStyle(status) {
    switch ((status || "").toUpperCase()) {
        case "DEPLOYED":
            return "success";
        case "INVENTORY":
            return "info";
        case "SHIPPED":
            return "primary";
        case "ARRIVED":
            return "warning";
        case "REPAIR":
            return "warning";
        case "RETIRED":
            return "default";
        default:
            return "default";
    }
}

export function softwareUpdates(unit, cap) {
    return unit?.updates || cap?.updates || {};
}

const AMERICA_TZ_PREFIX = "America/";

export function formatTimezoneDisplay(tz) {
    if (tz == null) return null;
    const s = String(tz).trim();
    if (!s) return null;
    const rest = s.startsWith(AMERICA_TZ_PREFIX) ? s.slice(AMERICA_TZ_PREFIX.length) : s;
    return rest || null;
}

export function softwareStatusStyle(status) {
    if (status === "FAILURE") return "danger";
    if (status === "UPDATE_AVAILABLE") return "warning";
    if (status === "UPDATED") return "success";
    return "default";
}

export function healthStyle(health) {
    if (health === "ONLINE") return "success";
    if (health === "BATTERY") return "warning";
    if (health === "POWER_LOSS" || health === "ERROR") return "danger";
    return "default";
}

export function yesNo(value) {
    if (value === true) return "Yes";
    if (value === false) return "No";
    return "—";
}

export function httpsKeysDisplay(httpsKeys) {
    if (!httpsKeys || typeof httpsKeys !== "object") return {text: "—", bsStyle: "default"};
    const lastUpdated = httpsKeys.last_updated ? new Date(httpsKeys.last_updated).getTime() : 0;
    const updateAvailable = httpsKeys.update_available ? new Date(httpsKeys.update_available).getTime() : 0;
    if (!updateAvailable) return {text: "Updated", bsStyle: "success"};
    if (lastUpdated >= updateAvailable) return {text: "Updated", bsStyle: "success"};
    return {text: "Pending", bsStyle: "warning"};
}

export function formatUpdateAvailableDate() {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    const h = String(now.getHours()).padStart(2, "0");
    const min = String(now.getMinutes()).padStart(2, "0");
    const s = String(now.getSeconds()).padStart(2, "0");
    const ms = String(now.getMilliseconds()).padStart(3, "0");
    return `${y}-${m}-${d} ${h}:${min}:${s}.${ms}000`;
}

export function apiErrorMessage(err) {
    return (
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        err?.message ||
        "Request failed"
    );
}
